import { motion } from "framer-motion";
import { Users, CheckCircle, XCircle, Clock, AlertTriangle, Shield } from "lucide-react";

const BentoCard = ({ title, items, delay, accentColor }) => (
  <motion.div
    initial={{ opacity: 0, y: 15 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.25 }}
    className="surface-card p-5 relative card-accent-top"
    data-testid={`bento-${title.toLowerCase().replace(/\s/g, '-')}`}
  >
    <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.2em] mb-4">{title}</p>
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.label} className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 flex items-center justify-center border ${item.iconBg}`}>
              <item.icon className={`w-3.5 h-3.5 ${item.iconColor}`} />
            </div>
            <span className="text-xs text-zinc-400 font-medium">{item.label}</span>
          </div>
          <span className={`font-mono text-xl font-bold tracking-wider ${item.valueColor}`}>
            {item.value}
          </span>
        </div>
      ))}
    </div>
  </motion.div>
);

const StatsGrid = ({ stats }) => {
  if (!stats) return null;

  const cards = [
    {
      title: "Personnel",
      items: [
        {
          icon: Users,
          label: "Total Staff",
          value: stats.total_staff,
          iconColor: "text-[#00E5FF]",
          iconBg: "border-[#00E5FF]/30 bg-[#00E5FF]/10",
          valueColor: "text-[#00E5FF]",
        },
        {
          icon: CheckCircle,
          label: "Active",
          value: stats.passed_count,
          iconColor: "text-emerald-400",
          iconBg: "border-emerald-500/30 bg-emerald-500/10",
          valueColor: "text-emerald-400",
        },
      ],
    },
    {
      title: "Quota Status",
      items: [
        {
          icon: XCircle,
          label: "Failed",
          value: stats.failed_count,
          iconColor: "text-[#FF3366]",
          iconBg: "border-[#FF3366]/30 bg-[#FF3366]/10",
          valueColor: "text-[#FF3366]",
        },
        {
          icon: Clock,
          label: "Exempt",
          value: stats.exempt_count,
          iconColor: "text-amber-400",
          iconBg: "border-amber-500/30 bg-amber-500/10",
          valueColor: "text-amber-400",
        },
      ],
    },
    {
      title: "Alerts",
      items: [
        {
          icon: AlertTriangle,
          label: "Strikes",
          value: stats.strikes_count,
          iconColor: "text-orange-400",
          iconBg: "border-orange-500/30 bg-orange-500/10",
          valueColor: "text-orange-400",
        },
        {
          icon: AlertTriangle,
          label: "Warnings",
          value: stats.warnings_count,
          iconColor: "text-yellow-400",
          iconBg: "border-yellow-500/30 bg-yellow-500/10",
          valueColor: "text-yellow-400",
        },
      ],
    },
    {
      title: "Status",
      items: [
        {
          icon: Clock,
          label: "On LOA",
          value: stats.on_loa_count,
          iconColor: "text-cyan-400",
          iconBg: "border-cyan-400/30 bg-cyan-400/10",
          valueColor: "text-cyan-400",
        },
        {
          icon: Shield,
          label: "Suspended",
          value: stats.suspended_count,
          iconColor: "text-rose-400",
          iconBg: "border-rose-500/30 bg-rose-500/10",
          valueColor: "text-rose-400",
        },
      ],
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="stats-grid">
      {cards.map((card, i) => (
        <BentoCard key={card.title} title={card.title} items={card.items} delay={i * 0.05} />
      ))}
    </div>
  );
};

export default StatsGrid;
